import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Description as DocumentIcon } from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { getRuntimeCapabilities } from '../../api/runtime/capabilities';
import { useActiveDocumentContext } from '../../hooks/ActiveDocumentProvider';

interface RuntimeModeBadgeProps {
  compact?: boolean;
  /** When false, only the runtime capability chip is shown (document is rendered separately, e.g. in MainLayout). */
  showDocument?: boolean;
}

const RuntimeModeBadge: React.FC<RuntimeModeBadgeProps> = ({ compact = false, showDocument = true }) => {
  const theme = useTheme();
  const capabilities = getRuntimeCapabilities();
  const documentName = useActiveDocumentContext();

  const modeStyle = (() => {
    switch (capabilities.mode) {
      case 'hostObject':
        return { label: 'HostObject', color: theme.palette.success.main };
      case 'grpc':
        return { label: 'gRPC', color: theme.palette.info.main };
      default:
        return { label: 'Mock', color: theme.palette.warning.main };
    }
  })();

  const unsupported = [
    !capabilities.canConfigureMappings ? 'mapping config' : null,
    !capabilities.canHighlightElements ? 'highlight' : null,
    !capabilities.canReconnect ? 'reconnect' : null,
    !capabilities.canQuickFix ? 'quick-fix' : null,
  ].filter(Boolean) as string[];

  const tooltipText =
    unsupported.length > 0
      ? `Mode: ${modeStyle.label}. Limited: ${unsupported.join(', ')}`
      : `Mode: ${modeStyle.label}. Full capability set.`;

  return (
    <Stack direction="row" spacing={compact ? 0.625 : 1} alignItems="center">
      {showDocument && documentName && (
        <Stack direction="row" spacing={layoutGap.dense} alignItems="center" sx={{ minWidth: 0 }}>
          <DocumentIcon sx={{ fontSize: compact ? 12 : 14, color: theme.palette.info.main, flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.62rem' : '0.68rem',
              color: theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: compact ? 150 : 200,
            }}
            title={documentName}
          >
            {documentName}
          </Typography>
        </Stack>
      )}
      <Tooltip title={tooltipText}>
        <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: compact ? '0.62rem' : '0.68rem' }}
          >
            Runtime
          </Typography>
          <Chip
            label={modeStyle.label}
            size="small"
            sx={{
              height: compact ? 14 : 16,
              fontSize: compact ? '0.54rem' : '0.58rem',
              fontWeight: 700,
              backgroundColor: alpha(modeStyle.color, 0.12),
              color: modeStyle.color,
              borderRadius: `${borderRadius.sm}px`,
              '& .MuiChip-label': { px: 0.45 },
            }}
          />
          {unsupported.length > 0 && (
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: theme.palette.warning.main,
              }}
            />
          )}
        </Stack>
      </Tooltip>
    </Stack>
  );
};

export default RuntimeModeBadge;
